import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Button, Spinner, Input } from 'reactstrap';

const AiChatPanel = ({
  emailEditorRef,
  messagesSelector,
  loadingSelector,
  errorSelector,
  onSend,
  onUndo,
  onReset,
}) => {
  const { t } = useTranslation();
  const messages = useSelector(messagesSelector);
  const loading = useSelector(loadingSelector);
  const error = useSelector(errorSelector);
  const [prompt, setPrompt] = useState('');
  const messagesEndRef = useRef(null);
  const lastAppliedAssistantIdRef = useRef(null);
  const previousMessagesLengthRef = useRef(messages.length);

  const lastAssistantMessage = [...messages]
    .reverse()
    .find((m) => m.role === 'assistant');

  useEffect(() => {
    const previousLength = previousMessagesLengthRef.current;
    const hasNewMessage = messages.length > previousLength;
    previousMessagesLengthRef.current = messages.length;

    if (!hasNewMessage) return;
    if (!lastAssistantMessage?.design || !emailEditorRef.current?.editor)
      return;
    if (lastAppliedAssistantIdRef.current === lastAssistantMessage.id) return;

    lastAppliedAssistantIdRef.current = lastAssistantMessage.id;
    emailEditorRef.current.editor.loadDesign(lastAssistantMessage.design);
  }, [messages.length, lastAssistantMessage?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages.length, loading]);

  const handleSend = useCallback(() => {
    if (!prompt.trim() || loading || !emailEditorRef.current?.editor) return;
    const currentPrompt = prompt.trim();
    emailEditorRef.current.editor.exportHtml(({ design }) => {
      onSend(currentPrompt, design);
    });
    setPrompt('');
  }, [prompt, loading, emailEditorRef, onSend]);

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      e.stopPropagation();
      handleSend();
    }
  };

  const handleUndo = (message) => {
    onUndo(message);
  };

  const handleReset = () => {
    onReset();
  };

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        height: '80vh',
        border: '1px solid #e9e9ef',
        borderRadius: 4,
        background: '#fff',
        overflow: 'hidden',
      }}
    >
      {/* Header */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '10px 14px',
          borderBottom: '1px solid #e9e9ef',
          background: '#f8f8fb',
        }}
      >
        <span className="font-size-13 font-weight-semibold">
          <i className="bx bx-bot mr-1 text-primary" />
          {t('AI Assistant')}
        </span>
        {messages.length > 0 && !loading && (
          <Button
            color="link"
            size="sm"
            className="p-0 text-muted font-size-11"
            onClick={handleReset}
            title={t('Clear conversation')}
          >
            <i className="bx bx-trash" />
          </Button>
        )}
      </div>

      {/* Messages */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '12px' }}>
        {messages.length === 0 && !loading && (
          <>
            <p className="text-muted font-size-12 text-center mt-3 mb-3">
              {t(
                'Describe the DEM template you want to create or how to modify the current one.',
              )}
            </p>
            <div
              style={{
                background: '#f8f8fb',
                border: '1px solid #e9e9ef',
                borderRadius: 4,
                padding: '10px 12px',
                fontSize: 11,
                color: '#74788d',
              }}
            >
              <p className="mb-1 font-weight-semibold">
                <i className="bx bx-info-circle mr-1" />
                {t('Limitations')}
              </p>
              <ul className="mb-0 pl-3">
                <li>{t('Does not analyze external URLs or images')}</li>
                <li>{t('Does not read attached files or documents')}</li>
                <li>{t('Conversation is not saved when closed')}</li>
                <li>{t('Complex templates take 30–60 seconds')}</li>
                <li>{t('May require a few iterations for optimal results')}</li>
              </ul>
            </div>
          </>
        )}

        {messages.map((msg) => (
          <div
            key={msg.id}
            className={`mb-2 ${msg.role === 'user' ? 'text-right' : ''}`}
          >
            <div
              style={{
                display: 'inline-block',
                maxWidth: '90%',
                padding: '8px 12px',
                borderRadius: msg.role === 'user' ? '12px 12px 2px 12px' : '12px 12px 12px 2px',
                background: msg.role === 'user' ? '#556ee6' : '#f8f8fb',
                color: msg.role === 'user' ? '#fff' : '#495057',
                fontSize: 12,
                textAlign: 'left',
                border: msg.role === 'assistant' ? '1px solid #e9e9ef' : 'none',
              }}
            >
              {msg.content}
              {msg.role === 'assistant' && msg.design && (
                <div className="mt-2">
                  <Button
                    color="secondary"
                    outline
                    size="sm"
                    className="font-size-11 py-0 px-1"
                    disabled={!msg.designSnapshotBefore}
                    onClick={() => handleUndo(msg)}
                    title={
                      msg.designSnapshotBefore
                        ? t('Restore previous design')
                        : t('No previous design')
                    }
                  >
                    <i className="bx bx-undo mr-1" />
                    {t('Cancel')}
                  </Button>
                </div>
              )}
            </div>
          </div>
        ))}

        {loading && (
          <div className="mb-2">
            <div
              style={{
                display: 'inline-block',
                padding: '8px 12px',
                borderRadius: '12px 12px 12px 2px',
                background: '#f8f8fb',
                border: '1px solid #e9e9ef',
                fontSize: 12,
              }}
            >
              <Spinner size="sm" color="primary" />
              <span className="ml-2 text-muted">{t('Generating...')}</span>
            </div>
          </div>
        )}

        {error && !loading && (
          <p className="text-danger font-size-11 mt-1">{error}</p>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <div
        style={{
          padding: '10px 12px',
          borderTop: '1px solid #e9e9ef',
          background: '#f8f8fb',
        }}
      >
        <Input
          type="textarea"
          rows={3}
          value={prompt}
          onChange={(e) => setPrompt(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={t('Describe the template... (Cmd+Enter to send)')}
          disabled={loading}
          style={{ fontSize: 12, resize: 'none' }}
        />
        <Button
          color="primary"
          size="sm"
          block
          className="mt-2"
          disabled={!prompt.trim() || loading}
          onClick={handleSend}
        >
          {loading ? (
            <Spinner size="sm" color="light" />
          ) : (
            <>
              <i className="bx bx-send mr-1" />
              {t('Send')}
            </>
          )}
        </Button>
      </div>
    </div>
  );
};

export default AiChatPanel;
